import type {
  BuildingBlock,
  ModelDocument,
  ModelType,
  Dialect,
  DomainObject,
  Operation,
  Diagnostic,
  TypeSpec,
  Relationship,
} from "../shared/contracts.js";
import { blockDefinitions as baselineDefinitions, type BlockEntity } from "./block-definitions.js";
import { industryCatalogue, industryDefinitions } from "./block-packs/index.js";
import { corporateDefinitions, corporateModels } from "./block-packs/corporate.js";
export const blockDefinitions: Record<string, BlockEntity[]> = { ...baselineDefinitions, ...industryDefinitions, ...corporateDefinitions };
import {
  clone,
  createModel,
  createObject,
  uid,
  syncDocument,
  validateModel,
  DomainError,
} from "./model.js";
import { applyOperations } from "./operations.js";
import { transformModel } from "./transformations.js";
import { sha256 } from "./digest.js";

type BlockMeta = Pick<BuildingBlock, "id" | "name" | "category" | "description" | "dependencies" | "group" | "industry" | "tags" | "sourceReferences" | "assumptions">;
const meta: BlockMeta[] = [
  {
    id: "party",
    name: "Party and party roles",
    category: "Foundation",
    description:
      "People and organizations, identifiers, contextual roles, contacts and addresses. Names and email addresses are not unique identities.",
    dependencies: [],
  },
  {
    id: "identity-access",
    name: "Identity and access",
    category: "Foundation",
    description:
      "Fourteen entities for accounts, roles, permissions, resources, groups, federation and declared SSO/MFA policy. This models application access; it does not grant Studio access.",
    dependencies: [],
  },
  {
    id: "organization-employee",
    name: "Organization and employee",
    category: "People",
    description:
      "Ten entities distinguish employee identity, employment occurrences, positions, assignments and reporting relationships. Employees do not require application accounts.",
    dependencies: [],
  },
  {
    id: "talent",
    name: "Talent acquisition",
    category: "People",
    description:
      "Requisitions, candidates, applications, stage history, assessments, offers and an explicit employment handoff.",
    dependencies: ["party", "organization-employee"],
  },
  {
    id: "benefits",
    name: "Employee benefits",
    category: "People",
    description:
      "Benefit plans, eligibility decisions, enrollment, dependant relationships, coverage and dated contributions.",
    dependencies: ["party", "organization-employee"],
  },
  {
    id: "customer",
    name: "Customer",
    category: "Commerce",
    description:
      "Organization-scoped commercial customer accounts with party-role bindings, contacts, sites and dated credit/payment policy.",
    dependencies: ["party", "organization-employee"],
  },
  {
    id: "supplier",
    name: "Supplier",
    category: "Commerce",
    description:
      "Organization-scoped supplier accounts, contacts, sites and dated procurement policy. A Party may be both supplier and customer.",
    dependencies: ["party", "organization-employee"],
  },
  {
    id: "sales",
    name: "Sales orders and fulfillment",
    category: "Commerce",
    description:
      "Orders, order lines, address snapshots, partial allocation and shipment, returns and inventory references.",
    dependencies: ["customer", "inventory"],
  },
  {
    id: "inventory",
    name: "Inventory",
    category: "Operations",
    description:
      "Items, sites, stock locations, lots, accepted stock movements, reservations and a derived stock-position design.",
    dependencies: ["organization-employee"],
  },
  {
    id: "finance",
    name: "Basic finance",
    category: "Finance",
    description:
      "Chart/accounts, ledger, journals, debtor/creditor subsidiaries, cash/bank, statements, matching and effective P&L mappings. Balances derive from posted activity.",
    dependencies: ["customer", "supplier"],
  },
  {
    id: "assets",
    name: "Assets and maintenance",
    category: "Operations",
    description:
      "Individual assets, maintenance plans, work orders, tasks, service events and meter readings.",
    dependencies: ["organization-employee"],
  },
  {
    id: "projects",
    name: "Projects and work",
    category: "Operations",
    description:
      "Projects, work hierarchy and dependencies, employment assignments, actual work and status history.",
    dependencies: ["organization-employee"],
  },
];
for (const block of meta) {
  block.tags = [block.category, block.name, ...(block.id === "organization-employee" ? ["HR", "Human resources", "Employment"] : [])];
  block.group = ["party", "identity-access"].includes(block.id) ? "foundation" : "corporate";
  block.category = block.group === "foundation" ? "Shared foundations" : "Corporate functions";
}
meta.push(...corporateModels);
meta.push(...industryCatalogue(baselineDefinitions).map(({ entities, ...block }) => ({
  ...block, group: "industry" as const, category: "Industry domains",
})));
export type DependencyChoice =
  { action: "create" } | { action: "reuse"; instanceId: string };
export interface InstantiateOptions {
  prefix?: string;
  dependencies?: Record<string, DependencyChoice>;
  bindings?: Record<string, string>;
  keyStrategy?: "business-primary" | "surrogate-primary";
}
export interface BlockInstance {
  instanceId: string;
  packageId: string;
  version: string;
  objectMap: Record<string, string>;
  dependencyInstances: Record<string, string>;
}
const version = "1.0.0";
const blockVersion = (id: string) => id.startsWith("BB-") ? "2.5.0" : version;
const definitionsByName = new Map(
  Object.entries(blockDefinitions).flatMap(([blockId, entities]) =>
    entities.map((e) => [e.name, { ...e, blockId }] as const),
  ),
);
const pkOf = (entity: BlockEntity) =>
  entity.keys.find((k) => k.role === "primary")?.members ?? [];
const fieldType = (domain: string, dialect: Dialect): TypeSpec => {
  const name = domain.split("(")[0].toUpperCase(),
    args =
      domain
        .match(/\(([^)]+)\)/)?.[1]
        .split(",")
        .map(Number) ?? [];
  if (name === "ID")
    return dialect === "postgresql"
      ? { name: "UUID" }
      : dialect === "sqlserver"
        ? { name: "UNIQUEIDENTIFIER" }
        : { name: dialect === "oracle" ? "VARCHAR2" : "CHAR", length: 36 };
  if (name === "TEXT" || name === "CODE")
    return {
      name:
        dialect === "oracle"
          ? "VARCHAR2"
          : dialect === "sqlserver"
            ? "NVARCHAR"
            : "VARCHAR",
      length: args[0] ?? 255,
    };
  if (name === "BOOL")
    return dialect === "oracle"
      ? { name: "NUMBER", precision: 1, scale: 0 }
      : {
          name:
            dialect === "postgresql" || dialect === "mysql" ? "BOOLEAN" : "BIT",
        };
  if (name === "DATE") return { name: "DATE" };
  if (name === "INSTANT")
    return {
      name:
        dialect === "postgresql"
          ? "TIMESTAMPTZ"
          : dialect === "oracle"
            ? "TIMESTAMP WITH TIME ZONE"
            : dialect === "sqlserver"
              ? "DATETIME2"
              : "DATETIME",
    };
  if (name === "INTEGER")
    return {
      name: dialect === "oracle" ? "NUMBER" : "INTEGER",
      ...(dialect === "oracle" ? { precision: 10, scale: 0 } : {}),
    };
  const [precision, scale] =
    name === "MONEY"
      ? [20, 4]
      : name === "QTY"
        ? [20, 6]
        : name === "PCT"
          ? [7, 4]
          : args.length
            ? args
            : [18, 2];
  return {
    name: dialect === "oracle" ? "NUMBER" : "DECIMAL",
    precision,
    scale,
  };
};
// Template ports are declared by their stable package/table/field keys. These mappings
// are part of the bundled definitions, never inferred by matching destination names.
const targetOverrides: Record<string, string> = {
  "iam_resource.parent_resource_id": "iam_resource.resource_id",
  "iam_external_identity.provider_id": "iam_identity_provider.provider_id",
  "org_unit.parent_unit_id": "org_unit.unit_id",
  "org_reporting_line.subordinate_employment_id":
    "org_employment.employment_id",
  "org_reporting_line.manager_employment_id": "org_employment.employment_id",
  "pty_person.party_id": "pty_party.party_id",
  "pty_organization.party_id": "pty_party.party_id",
  "pty_role.scope_party_id": "pty_party.party_id",
  "tal_candidate.person_party_id": "pty_person.party_id",
  "ben_dependant.person_party_id": "pty_person.party_id",
  "ben_covered_person.person_party_id": "pty_person.party_id",
  "ben_plan.provider_party_id": "pty_party.party_id",
  "cus_contact.person_party_id": "pty_person.party_id",
  "sup_contact.person_party_id": "pty_person.party_id",
  "inv_site.location_id": "org_location.location_id",
  "inv_movement.reverses_movement_id": "inv_movement.movement_id",
  "inv_movement_line.from_location_id": "inv_location.stock_location_id",
  "inv_movement_line.to_location_id": "inv_location.stock_location_id",
  "sal_order_address.customer_site_id": "cus_site.customer_site_id",
  "sal_order_line.item_id": "inv_item.item_id",
  "sal_allocation.reservation_id": "inv_reservation.reservation_id",
  "sal_shipment_line.movement_line_id": "inv_movement_line.movement_line_id",
  "sal_return_line.movement_line_id": "inv_movement_line.movement_line_id",
  "fin_journal.reverses_journal_id": "fin_journal.journal_id",
  "fin_journal_line.account_id": "fin_account.account_id",
  "fin_debtor_account.control_account_id": "fin_account.account_id",
  "fin_creditor_account.control_account_id": "fin_account.account_id",
  "fin_cash_account.gl_account_id": "fin_account.account_id",
  "fin_account_report_map.account_id": "fin_account.account_id",
  "fin_settlement.source_receivable_credit_id":
    "fin_receivable_item.receivable_item_id",
  "fin_settlement.source_payable_credit_id": "fin_payable_item.payable_item_id",
  "fin_settlement.reverses_settlement_id": "fin_settlement.settlement_id",
  "fin_settlement_allocation.reverses_allocation_id":
    "fin_settlement_allocation.allocation_id",
  "fin_report_line.parent_line_id": "fin_report_line.report_line_id",
  "ast_asset.location_id": "org_location.location_id",
  "prj_work_item.parent_work_item_id": "prj_work_item.work_item_id",
  "prj_dependency.predecessor_work_item_id": "prj_work_item.work_item_id",
  "prj_dependency.successor_work_item_id": "prj_work_item.work_item_id",
};
const standardTargets: Record<string, string> = {
  realm_id: "iam_realm.realm_id",
  provider_id: "iam_identity_provider.provider_id",
  account_id: "iam_account.account_id",
  role_id: "iam_role.role_id",
  resource_id: "iam_resource.resource_id",
  action_id: "iam_action.action_id",
  permission_id: "iam_permission.permission_id",
  group_id: "iam_security_group.group_id",
  organization_id: "org_organization.organization_id",
  unit_id: "org_unit.unit_id",
  position_id: "org_position.position_id",
  employee_id: "org_employee.employee_id",
  employment_id: "org_employment.employment_id",
  type_code: "org_employment_type.type_code",
  location_id: "org_location.location_id",
  party_id: "pty_party.party_id",
  party_role_id: "pty_role.party_role_id",
  role_type_code: "pty_role_type.role_type_code",
  person_party_id: "pty_person.party_id",
  address_id: "pty_address.address_id",
  requisition_id: "tal_requisition.requisition_id",
  candidate_id: "tal_candidate.candidate_id",
  application_id: "tal_application.application_id",
  stage_id: "tal_stage.stage_id",
  offer_id: "tal_offer.offer_id",
  plan_id: "ben_plan.plan_id",
  option_id: "ben_option.option_id",
  enrollment_id: "ben_enrollment.enrollment_id",
  dependant_id: "ben_dependant.dependant_id",
  customer_id: "cus_account.customer_id",
  supplier_id: "sup_account.supplier_id",
  site_id: "inv_site.site_id",
  stock_location_id: "inv_location.stock_location_id",
  item_id: "inv_item.item_id",
  lot_id: "inv_lot.lot_id",
  movement_id: "inv_movement.movement_id",
  reservation_id: "inv_reservation.reservation_id",
  order_id: "sal_order.order_id",
  order_line_id: "sal_order_line.order_line_id",
  shipment_id: "sal_shipment.shipment_id",
  shipment_line_id: "sal_shipment_line.shipment_line_id",
  return_id: "sal_return.return_id",
  chart_id: "fin_chart.chart_id",
  ledger_id: "fin_ledger.ledger_id",
  period_id: "fin_period.period_id",
  journal_id: "fin_journal.journal_id",
  journal_line_id: "fin_journal_line.journal_line_id",
  debtor_account_id: "fin_debtor_account.debtor_account_id",
  creditor_account_id: "fin_creditor_account.creditor_account_id",
  receivable_item_id: "fin_receivable_item.receivable_item_id",
  payable_item_id: "fin_payable_item.payable_item_id",
  settlement_id: "fin_settlement.settlement_id",
  cash_account_id: "fin_cash_account.cash_account_id",
  statement_id: "fin_bank_statement.statement_id",
  statement_line_id: "fin_bank_statement_line.statement_line_id",
  cash_movement_id: "fin_cash_movement.cash_movement_id",
  report_line_id: "fin_report_line.report_line_id",
  asset_id: "ast_asset.asset_id",
  maintenance_plan_id: "ast_plan.maintenance_plan_id",
  work_order_id: "ast_work_order.work_order_id",
  project_id: "prj_project.project_id",
  work_item_id: "prj_work_item.work_item_id",
  work_assignment_id: "prj_assignment.work_assignment_id",
};
function targetFor(entity: BlockEntity, column: string): string | undefined {
  const declared = entity.columns.find(field => field.name === column)?.reference;
  if (declared) return declared;
  if (entity.referenceMode === "explicit") return undefined;
  if (targetOverrides[`${entity.name}.${column}`])
    return targetOverrides[`${entity.name}.${column}`];
  const target = standardTargets[column];
  if (!target) return undefined;
  const [table] = target.split(".");
  if (table === entity.name) return undefined;
  // Account identifiers have different grains in IAM and Finance.
  if (column === "account_id" && !entity.name.startsWith("iam_"))
    return undefined;
  if (column === "type_code" && entity.name !== "org_employment")
    return undefined;
  return target;
}
function buildObjects(
  blockId: string,
  repId: string,
  kind: ModelType,
  dialect: Dialect,
  prefix: string,
  instanceId: string,
): { objects: DomainObject[]; map: Record<string, string> } {
  const objects: DomainObject[] = [],
    map: Record<string, string> = {};
  const version = blockVersion(blockId);
  for (const def of blockDefinitions[blockId]) {
    const root = createObject(
      kind === "physical"
        ? def.derived
          ? "view"
          : "table"
        : kind === "conceptual"
          ? "concept"
          : "entity",
      prefix + def.name,
      repId,
    );
    root.displayName = def.title;
    root.description = def.description;
    root.briefDescription =
      def.title +
      ". " +
      meta.find((m) => m.id === blockId)!.description.split(".")[0] +
      ".";
    root.extensions = {
      buildingBlock: {
        packageId: blockId,
        version,
        instanceId,
        objectKey: def.name,
      },
      businessRules: {
        description: def.rules,
        enforcement: "modeled intent; target runtime validation required",
      },
    };
    if (def.derived) {
      root.extensions.derived = {
        readOnly: true,
        rule: def.rules,
        sqlStatus: "requires reviewed target SQL",
      };
      if (kind === "physical") root.sql = "";
    }
    map[def.name] = root.objectId;
    objects.push(root);
    for (const [ordinal, col] of def.columns.entries()) {
      const field = createObject(
        kind === "physical" ? "column" : "attribute",
        col.name,
        repId,
        root.objectId,
      );
      field.nullable = col.nullable;
      field.description = col.description;
      field.ordinal = ordinal;
      field.extensions = {
        buildingBlock: {
          packageId: blockId,
          version,
          instanceId,
          objectKey: `${def.name}.${col.name}`,
        },
        domainIntent: col.domain,
      };
      if (kind === "physical") field.type = fieldType(col.domain, dialect);
      else if (kind !== "conceptual")
        field.logicalType = col.domain.startsWith("CODE")
          ? "code"
          : col.domain.startsWith("TEXT")
            ? "text"
            : col.domain === "ID"
              ? "identifier"
              : col.domain.toLowerCase();
      map[`${def.name}.${col.name}`] = field.objectId;
      objects.push(field);
    }
    for (const [i, key] of def.keys.entries()) {
      const keyObject = createObject(
        "key",
        `${key.role === "primary" ? "pk" : "uq"}_${prefix}${def.name}_${i || ""}`.replace(
          /_$/,
          "",
        ),
        repId,
        root.objectId,
      );
      keyObject.members = key.members.map((n) => map[`${def.name}.${n}`]);
      keyObject.keyRole = key.role as "primary";
      keyObject.selectedPrimary = key.role === "primary";
      keyObject.description = `${key.role === "primary" ? "Selected package identifier" : "Alternate business or scope identifier"}: ${key.members.join(" + ")}. ${def.rules}`;
      keyObject.extensions = {
        buildingBlock: {
          packageId: blockId,
          version,
          instanceId,
          objectKey: `${def.name}:key:${i}`,
        },
      };
      map[`${def.name}:key:${i}`] = keyObject.objectId;
      objects.push(keyObject);
    }
  }
  return { objects, map };
}
function makeRelationships(
  blockIds: string[],
  maps: Record<string, Record<string, string>>,
  objects: DomainObject[],
  repId: string,
): { relationships: Relationship[]; keys: DomainObject[] } {
  const relationships: Relationship[] = [],
    keys: DomainObject[] = [],
    seen = new Set<string>();
  const lookup = (table: string, field?: string) => {
    const info = definitionsByName.get(table);
    return info
      ? maps[info.blockId]?.[field ? `${table}.${field}` : table]
      : undefined;
  };
  for (const block of blockIds)
    for (const def of blockDefinitions[block])
      for (const col of def.columns) {
        const target = targetFor(def, col.name);
        if (!target) continue;
        const [targetTable, targetColumn] = target.split("."),
          targetDef = definitionsByName.get(targetTable);
        if (!targetDef) continue;
        const sourceId = lookup(def.name),
          targetId = lookup(targetTable),
          sourceColumn = lookup(def.name, col.name),
          destinationColumn = lookup(targetTable, targetColumn);
        if (!sourceId || !targetId || !sourceColumn || !destinationColumn)
          continue;
        const sourceFields = [sourceColumn],
          targetFields = [destinationColumn];
        const scope = ["realm_id", "organization_id", "ledger_id"].find(
          (scope) =>
            scope !== col.name &&
            def.columns.some((c) => c.name === scope) &&
            targetDef.columns.some((c) => c.name === scope),
        );
        if (scope) {
          sourceFields.unshift(lookup(def.name, scope)!);
          targetFields.unshift(lookup(targetTable, scope)!);
        }
        const signature = `${sourceId}:${sourceFields.join(",")}:${targetId}:${targetFields.join(",")}`;
        if (seen.has(signature)) continue;
        seen.add(signature);
        if (
          ![...objects, ...keys].some(
            (o) =>
              o.kind === "key" &&
              o.ownerObjectId === targetId &&
              JSON.stringify(o.members) === JSON.stringify(targetFields),
          )
        ) {
          const key = createObject(
            "key",
            `uq_${targetTable}_${scope ?? targetColumn}_reference`,
            repId,
            targetId,
          );
          key.keyRole = "unique";
          key.members = targetFields;
          key.description =
            "Supporting scoped uniqueness for the declared package reference.";
          let num = 2;
          const name = key.name;
          while (
            [...objects, ...keys].some(
              (o) => o.ownerObjectId === targetId && o.name === key.name,
            )
          )
            key.name = `${name}_${num++}`;
          keys.push(key);
        }
        const id = uid();
        relationships.push({
          relationshipId: id,
          name: `fk_${objects.find((object) => object.objectId === sourceId)?.name ?? def.name}_${col.name}`,
          representationId: repId,
          sourceObjectId: sourceId,
          targetObjectId: targetId,
          sourceCardinality:
            (pkOf(def).length === 1 && pkOf(def)[0] === col.name) ||
            (def.referenceMode === "explicit" && def.keys.some(key =>
              ["primary", "unique", "business", "candidate"].includes(key.role) &&
              key.members.every(member => [col.name, ...(scope ? [scope] : [])].includes(member))))
              ? "one"
              : "many",
          targetCardinality: "one",
          sourceOptional: true,
          targetOptional: col.nullable,
          identifying: pkOf(def).includes(col.name),
          sourceAttributeIds: sourceFields,
          targetAttributeIds: targetFields,
          onDelete: "NO ACTION",
          onUpdate: "NO ACTION",
          description: `${def.title}: ${col.description}. Composite scope is preserved where declared.`,
          extensions: {
            buildingBlock: { packageId: block, version: blockVersion(block) },
            enforcement:
              "foreign key intent; cross-row, temporal and subtype business rules remain separately modeled",
          },
        });
      }
  return { relationships, keys };
}
function createBlock(
  blockId: string,
  modelType: ModelType,
  dialect: Dialect = "postgresql",
): ModelDocument {
  const version = blockVersion(blockId);
  const doc = createModel(
      meta.find((b) => b.id === blockId)!.name,
      modelType === "logical-physical" ? "logical" : modelType,
      "relational",
      dialect,
    ),
    rep = doc.model.activeRepresentation,
    instanceId = uid(),
    built = buildObjects(
      blockId,
      rep,
      modelType === "logical-physical" ? "logical" : modelType,
      dialect,
      "",
      instanceId,
    );
  doc.objects = built.objects;
  const refs = makeRelationships(
    [blockId],
    { [blockId]: built.map },
    doc.objects,
    rep,
  );
  doc.objects.push(...refs.keys);
  doc.relationships = refs.relationships;
  doc.diagrams[0].appearances = doc.objects
    .filter((o) => !o.ownerObjectId)
    .map((o, i) => ({
      appearanceId: uid(),
      objectId: o.objectId,
      position: { x: (i % 4) * 340, y: Math.floor(i / 4) * 400 },
      width: 270,
    }));
  doc.dependencies = [
    {
      instanceId,
      packageId: blockId,
      version,
      objectMap: built.map,
      dependencyInstances: {},
      support: "modeled",
      unresolvedDependencyPackages: meta.find((m) => m.id === blockId)!
        .dependencies,
    },
  ];
  syncDocument(doc);
  if (modelType === "logical-physical")
    return transformModel(doc, "logical-physical", dialect).document;
  return doc;
}
export const buildingBlocks: BuildingBlock[] = meta.map((block) => ({
  ...block,
  version: blockVersion(block.id),
  entityCount: blockDefinitions[block.id].length,
  create: (modelType: ModelType, dialect?: Dialect) =>
    createBlock(block.id, modelType, dialect),
}));
export function blockDependencyClosure(blockId: string): string[] {
  const found = new Set<string>(),
    visiting = new Set<string>(),
    order: string[] = [];
  const visit = (id: string) => {
    if (found.has(id)) return;
    if (visiting.has(id))
      throw new DomainError(
        "DEPENDENCY_CYCLE",
        "Building-block dependency cycle.",
      );
    const block = meta.find((b) => b.id === id);
    if (!block)
      throw new DomainError(
        "BLOCK_NOT_FOUND",
        `Unknown building block '${id}'.`,
      );
    visiting.add(id);
    block.dependencies.forEach(visit);
    visiting.delete(id);
    found.add(id);
    order.push(id);
  };
  visit(blockId);
  return order;
}
export function instantiateBlock(
  document: ModelDocument,
  blockId: string,
  options: InstantiateOptions = {},
): { operations: Operation[]; diagnostics: Diagnostic[] } {
  const diagnostics: Diagnostic[] = [],
    operations: Operation[] = [],
    rep = document.representations.find(
      (r) => r.representationId === document.model.activeRepresentation,
    );
  if (!rep)
    throw new DomainError("NOT_FOUND", "Active representation does not exist.");
  if (
    document.model.family !== "relational" ||
    !["conceptual", "logical", "physical"].includes(rep.kind)
  )
    return {
      operations,
      diagnostics: [
        {
          code: "BLOCK_FAMILY",
          severity: "error",
          message:
            "These building blocks require a conceptual, logical or physical relational representation.",
        },
      ],
    };
  const closure = blockDependencyClosure(blockId),
    maps: Record<string, Record<string, string>> = {},
    instances: Record<string, string> = {},
    newIds: string[] = [],
    allObjects = [...document.objects];
  for (const id of closure) {
    const choice =
      id === blockId
        ? { action: "create" as const }
        : options.dependencies?.[id];
    if (!choice) {
      diagnostics.push({
        code: "DEPENDENCY_DECISION_REQUIRED",
        severity: "error",
        message: `Choose Create or explicitly select an existing instance of '${meta.find((m) => m.id === id)!.name}'. Names do not establish identity.`,
        path: `dependencies.${id}`,
      });
      continue;
    }
    if (choice.action === "reuse") {
      const found = document.dependencies.find(
        (d) =>
          d.instanceId === choice.instanceId &&
          d.packageId === id &&
          d.version === blockVersion(id),
      ) as unknown as BlockInstance | undefined;
      if (!found || !found.objectMap) {
        diagnostics.push({
          code: "DEPENDENCY_INSTANCE_INVALID",
          severity: "error",
          message: `The selected ${id} instance is missing or has an incompatible version.`,
        });
        continue;
      }
      const invalid = Object.values(found.objectMap).some(
        (objectId) =>
          !document.objects.some(
            (o) =>
              o.objectId === objectId &&
              o.representationId === rep.representationId,
          ),
      );
      if (invalid) {
        diagnostics.push({
          code: "DEPENDENCY_REFERENCE_INVALID",
          severity: "error",
          message: `The ${id} instance contains deleted objects or belongs to another representation.`,
        });
        continue;
      }
      maps[id] = clone(found.objectMap);
      instances[id] = found.instanceId;
    } else if (choice.action === "create") {
      const instanceId = uid(),
        built = buildObjects(
          id,
          rep.representationId,
          rep.kind as ModelType,
          document.model.targetProfile ?? "postgresql",
          options.prefix ?? "",
          instanceId,
        );
      instances[id] = instanceId;
      maps[id] = built.map;
      newIds.push(id);
      allObjects.push(...built.objects);
      operations.push(
        ...built.objects.map((object) => ({ type: "object.create", object })),
      );
    } else
      diagnostics.push({
        code: "DEPENDENCY_CHOICE_INVALID",
        severity: "error",
        message: "Dependency action must be create or reuse.",
      });
  }
  if (diagnostics.some((d) => d.severity === "error"))
    return { operations: [], diagnostics };
  // Reconcile explicit selected IDs. Matching names never establishes a binding.
  if (options.bindings && Object.keys(options.bindings).length) {
    const removeIds = new Set<string>();
    for (const [id, templateMap] of Object.entries(maps))
      for (const def of blockDefinitions[id]) {
        const rootBinding = options.bindings[`${id}:${def.name}`];
        if (!rootBinding) continue;
        if (!newIds.includes(id)) {
          diagnostics.push({
            code: "PORT_BINDING_REUSE_CONFLICT",
            severity: "error",
            message:
              "Do not remap an already installed dependency instance during insertion.",
          });
          continue;
        }
        const root = document.objects.find(
            (o) =>
              o.objectId === rootBinding &&
              o.representationId === rep.representationId,
          ),
          generated = allObjects.find(
            (o) => o.objectId === templateMap[def.name],
          );
        if (!root || root.ownerObjectId || root.kind !== generated?.kind) {
          diagnostics.push({
            code: "PORT_BINDING_KIND",
            severity: "error",
            message: `Select an existing ${generated?.kind ?? "entity"} for ${def.title}.`,
          });
          continue;
        }
        const replacement: Record<string, string> = { [def.name]: rootBinding };
        let valid = true;
        for (const col of def.columns) {
          const fieldBinding =
              options.bindings[`${id}:${def.name}.${col.name}`],
            field = document.objects.find(
              (o) =>
                o.objectId === fieldBinding &&
                o.ownerObjectId === rootBinding &&
                o.representationId === rep.representationId,
            ),
            templateField = allObjects.find(
              (o) => o.objectId === templateMap[`${def.name}.${col.name}`],
            );
          if (!field || field.kind !== templateField?.kind) {
            diagnostics.push({
              code: "PORT_BINDING_FIELD",
              severity: "error",
              message: `Explicitly map ${def.name}.${col.name} to a compatible field of the selected entity.`,
            });
            valid = false;
            continue;
          }
          if (!col.nullable && field.nullable !== false) {
            diagnostics.push({
              code: "PORT_BINDING_NULLABILITY",
              severity: "error",
              message: `${def.name}.${col.name} is required but the selected field is optional.`,
            });
            valid = false;
          }
          if (
            rep.kind === "physical" &&
            JSON.stringify(field.type) !== JSON.stringify(templateField?.type)
          ) {
            diagnostics.push({
              code: "PORT_BINDING_TYPE",
              severity: "error",
              message: `${def.name}.${col.name} has a different physical type. An explicit type transformation is required before reuse.`,
            });
            valid = false;
          }
          replacement[`${def.name}.${col.name}`] = field.objectId;
        }
        for (const [index, key] of def.keys.entries()) {
          const members = key.members.map(
            (name) => replacement[`${def.name}.${name}`],
          );
          const existing = document.objects.find(
            (o) =>
              o.kind === "key" &&
              o.ownerObjectId === rootBinding &&
              JSON.stringify(o.members) === JSON.stringify(members) &&
              ["primary", "unique", "business", "candidate"].includes(
                o.keyRole ?? "",
              ),
          );
          if (!existing) {
            diagnostics.push({
              code: "PORT_BINDING_KEY",
              severity: "error",
              message: `The selected ${def.title} requires an existing ordered unique identifier (${key.members.join(", ")}).`,
            });
            valid = false;
          } else replacement[`${def.name}:key:${index}`] = existing.objectId;
        }
        if (valid) {
          for (const key of Object.keys(templateMap).filter(
            (key) =>
              key === def.name ||
              key.startsWith(def.name + ".") ||
              key.startsWith(def.name + ":key:"),
          ))
            removeIds.add(templateMap[key]);
          Object.assign(templateMap, replacement);
        }
      }
    for (const key of Object.keys(options.bindings)) {
      const split = key.indexOf(":"),
        id = key.slice(0, split),
        templateKey = key.slice(split + 1);
      if (!maps[id]?.[templateKey])
        diagnostics.push({
          code: "PORT_BINDING_INVALID",
          severity: "error",
          message: `Unknown template port '${key}'.`,
        });
    }
    if (diagnostics.some((d) => d.severity === "error"))
      return { operations: [], diagnostics };
    for (let i = operations.length - 1; i >= 0; i--)
      if (operations[i].object && removeIds.has(operations[i].object!.objectId))
        operations.splice(i, 1);
    for (let i = allObjects.length - 1; i >= 0; i--)
      if (removeIds.has(allObjects[i].objectId)) allObjects.splice(i, 1);
  }
  const refs = makeRelationships(
    newIds,
    maps,
    allObjects,
    rep.representationId,
  );
  // Generated package captions share a representation namespace. Reuse only
  // genuinely identical bound endpoint identities; names alone never bind.
  const existingRelationships = document.relationships.filter((relationship) => relationship.representationId === rep.representationId);
  const nameKey = (name: string) => name.normalize("NFKC").toLowerCase();
  const usedNames = new Set(existingRelationships.map((relationship) => nameKey(relationship.name)));
  const relationships = refs.relationships.filter((relationship) => !existingRelationships.some((existing) =>
    ["sourceObjectId", "targetObjectId", "sourceCardinality", "targetCardinality", "sourceOptional", "targetOptional", "identifying", "onDelete", "onUpdate"].every((key) => existing[key as keyof Relationship] === relationship[key as keyof Relationship]) &&
    JSON.stringify(existing.sourceAttributeIds) === JSON.stringify(relationship.sourceAttributeIds) &&
    JSON.stringify(existing.targetAttributeIds) === JSON.stringify(relationship.targetAttributeIds)));
  for (const relationship of relationships) {
    const base = relationship.name.slice(0, 500);
    let name = base, index = 2;
    while (usedNames.has(nameKey(name))) name = `${base}_${index++}`;
    relationship.name = name;
    usedNames.add(nameKey(name));
  }
  operations.push(
    ...refs.keys.map((object) => ({ type: "object.create", object })),
    ...relationships.map((relationship) => ({
      type: "relationship.create",
      relationship,
    })),
  );
  const diagram = document.diagrams.find(
    (d) => d.representationId === rep.representationId,
  );
  let position = 0;
  // Insert below existing geometry. A content pack must never cover or move an
  // unrelated entity merely because the same four-column layout was reused.
  const newRoots = allObjects.filter(o => !o.ownerObjectId && !document.objects.some(existing => existing.objectId === o.objectId));
  const estimatedHeight = (objectId: string) => Math.max(220, 100 + allObjects.filter(o => o.ownerObjectId === objectId && ["attribute", "column"].includes(o.kind)).length * 28);
  const rowStride = Math.max(440, ...newRoots.map(o => estimatedHeight(o.objectId) + 100));
  const insertionTop = diagram?.appearances.length
    ? Math.max(...diagram.appearances.map(a => a.position.y + (a.height ?? estimatedHeight(a.objectId)))) + 120
    : 0;
  if (diagram)
    for (const id of newIds) {
      const added = operations.filter(
        (o) =>
          o.type === "object.create" &&
          o.object &&
          !o.object.ownerObjectId &&
          (o.object.extensions.buildingBlock as any)?.packageId === id,
      );
      for (const op of added) {
        operations.push({
          type: "appearance.create",
          diagramId: diagram.diagramId,
          appearance: {
            appearanceId: uid(),
            objectId: op.object!.objectId,
            position: {
              x: (position % 4) * 340,
              y: insertionTop + Math.floor(position / 4) * rowStride,
            },
            width: 270,
          },
        });
        position++;
      }
    }
  for (const id of newIds) {
    const block = meta.find((m) => m.id === id)!;
    operations.push({
      type: "dependency.add",
      dependency: {
        instanceId: instances[id],
        packageId: id,
        version: blockVersion(id),
        definitionDigest: sha256(JSON.stringify(blockDefinitions[id])),
        objectMap: maps[id],
        dependencyInstances: Object.fromEntries(
          block.dependencies.map((d) => [d, instances[d]]),
        ),
        insertedAt: new Date().toISOString(),
        parameters: { prefix: options.prefix ?? "" },
        bindings: options.bindings ?? {},
        support: "modeled",
        assumptions: [
          ...(block.assumptions ?? []),
          "Domain template, not a target application runtime",
          "Temporal, cross-row and business rules require application/target enforcement",
          "No passwords, private records or credentials are included",
        ],
      },
    });
  }
  try {
    const proposed = applyOperations(document, operations).document;
    diagnostics.push(
      ...validateModel(proposed).filter((d) => d.severity === "error"),
    );
  } catch (error) {
    const e = error as DomainError;
    if (Array.isArray(e.details))
      diagnostics.push(...(e.details as Diagnostic[]));
    else
      diagnostics.push({
        code: e.code ?? "BLOCK_INVALID",
        severity: "error",
        message: e.message,
      });
  }
  diagnostics.push({
    code: "BLOCK_ENFORCEMENT_SCOPE",
    severity: "info",
    message:
      "Keys and declared relationships are modeled. Cross-row, temporal, finance posting, subtype and authentication behavior must be enforced by the target application. Derived stock/GL views require reviewed query definitions.",
    helpTopic: "building-blocks",
  });
  return {
    operations: diagnostics.some((d) => d.severity === "error")
      ? []
      : operations,
    diagnostics,
  };
}
